import { Component, EventEmitter, Input, Output, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface GalleryImage {
  id: number;
  src: string;
  alt: string;
}

@Component({
  selector: 'app-hotel-gallery-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="gallery-modal-overlay" (click)="close()">
      <div class="gallery-modal-container" (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="gallery-header">
          <div class="header-content">
            <span class="subtitle">Nuestros Espacios</span>
            <h2>Galería del Hotel</h2>
          </div>
          <button class="close-btn" (click)="close()" aria-label="Cerrar galería">
            <svg viewBox="0 0 24 24" fill="none" width="24" height="24">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Gallery Grid -->
        <div class="gallery-scroll-area">
          <div class="gallery-grid">
            @for (image of images; track image.id) {
              <div class="gallery-item" (click)="openLightbox(image)">
                <div class="image-wrapper">
                  <img [src]="image.src" [alt]="image.alt" (error)="handleImageError($event)">
                  <div class="image-overlay">
                    <span class="zoom-icon">🔍</span>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Lightbox (Zoomed View) -->
      @if (selectedImage()) {
        <div class="lightbox-overlay" (click)="closeLightbox(); $event.stopPropagation()">
          <button class="lightbox-close" (click)="closeLightbox()">×</button>
          <div class="lightbox-content" (click)="$event.stopPropagation()">
            <img [src]="selectedImage()?.src" [alt]="selectedImage()?.alt">
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      --color-primary: #C86A4A;
      --color-neutral: #A47551;
      --glass-bg: rgba(255, 255, 255, 0.85);
      --glass-border: rgba(255, 255, 255, 0.5);
      --shadow-premium: 0 20px 50px rgba(0, 0, 0, 0.15);
    }

    .gallery-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(8px);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.3s ease;
    }

    .gallery-modal-container {
      width: 100%;
      max-width: 1200px;
      max-height: 90vh;
      background: var(--glass-bg);
      border: 1px solid var(--glass-border);
      border-radius: 24px;
      box-shadow: var(--shadow-premium);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    }

    /* Header */
    .gallery-header {
      padding: 30px 40px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    .header-content .subtitle {
      color: var(--color-primary);
      text-transform: uppercase;
      letter-spacing: 2px;
      font-size: 0.8rem;
      font-weight: 600;
      display: block;
      margin-bottom: 8px;
    }

    .header-content h2 {
      margin: 0;
      font-family: 'Playfair Display', serif;
      font-size: 2rem;
      color: #333;
    }

    .close-btn {
      background: rgba(0, 0, 0, 0.05);
      border: none;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #666;
      transition: all 0.3s ease;
    }

    .close-btn:hover {
      background: var(--color-primary);
      color: white;
      transform: rotate(90deg);
    }

    /* Scroll Area */
    .gallery-scroll-area {
      flex: 1;
      overflow-y: auto;
      padding: 40px;
      scrollbar-width: thin;
      scrollbar-color: var(--color-primary) rgba(0, 0, 0, 0.05);
    }

    .gallery-scroll-area::-webkit-scrollbar {
      width: 6px;
    }

    .gallery-scroll-area::-webkit-scrollbar-thumb {
      background: var(--color-primary);
      border-radius: 10px;
    }

    /* Grid */
    .gallery-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 20px;
    }

    .gallery-item {
      aspect-ratio: 4/3;
      border-radius: 16px;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      background: #eee;
    }

    .image-wrapper {
      width: 100%;
      height: 100%;
      position: relative;
    }

    .image-wrapper img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .gallery-item:hover .image-wrapper img {
      transform: scale(1.1);
    }

    .image-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.7));
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: 20px;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .gallery-item:hover .image-overlay {
      opacity: 1;
    }


    .zoom-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(0.5);
      font-size: 1.5rem;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(4px);
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.3);
      opacity: 0;
      transition: all 0.3s ease;
    }

    .gallery-item:hover .zoom-icon {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }

    /* Lightbox */
    .lightbox-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      z-index: 2100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      animation: fadeIn 0.3s ease;
    }

    .lightbox-content {
      position: relative;
      max-width: 90vw;
      max-height: 80vh;
      animation: zoomIn 0.3s ease;
    }

    .lightbox-content img {
      max-width: 100%;
      max-height: 100%;
      border-radius: 8px;
    }


    .lightbox-close {
      position: absolute;
      top: 30px;
      right: 30px;
      background: none;
      border: none;
      color: white;
      font-size: 3rem;
      line-height: 1;
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.3s;
    }

    .lightbox-close:hover {
      opacity: 1;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideIn {
      from { transform: translateY(30px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    @keyframes zoomIn {
      from { transform: scale(0.9); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }

    @media (max-width: 768px) {
      .gallery-header { padding: 20px 25px; }
      .gallery-scroll-area { padding: 20px; }
      .header-content h2 { font-size: 1.5rem; }
      .gallery-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
    }
  `]
})
export class HotelGalleryModalComponent implements OnInit, OnDestroy {
  @Input() images: GalleryImage[] = [];
  @Output() closeEvent = new EventEmitter<void>();

  selectedImage = signal<GalleryImage | null>(null);

  ngOnInit() {
    document.body.style.overflow = 'hidden';
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
  }

  close() {
    this.closeEvent.emit();
  }

  openLightbox(image: GalleryImage) {
    this.selectedImage.set(image);
  }

  closeLightbox() {
    this.selectedImage.set(null);
  }

  handleImageError(event: any) {
    event.target.src = '/Fotos/hotel-andino.jpg'; // Fallback
  }
}
